import { Injectable, signal } from '@angular/core';

/**
 * Loading context metadata for HTTP requests
 *
 * Provides contextual information about the loading operation for fine-grained control and tracking.
 */
export interface LoadingContext {
  /** Feature or module initiating the request (e.g., 'member-info', 'clients') */
  feature?: string;

  /** Specific action being performed (e.g., 'fetch', 'submit', 'delete') */
  action?: string;

  /** Custom loading message to override the default */
  message?: string;

  /** HTTP method (GET, POST, etc.) */
  method?: string;

  /** Request URL */
  url?: string;
}

/** Storage key for persisting global loader debug message toggle in developer settings */
export const STORAGE_KEY_GLOBAL_LOADER_DEBUG_MESSAGE = 'symphony.globalLoader.debugMessage';

/**
 * Global Loading Service
 *
 * Centralized service for managing application-wide loading state. Used by HTTP interceptors and components to
 * show/hide loading indicators.
 *
 * Features:
 *
 * - Request counting to handle concurrent API calls
 * - Debounce support to prevent flashing for fast requests
 * - Optional loading messages with context metadata
 * - Signal-based reactive state
 * - Contextual tracking for fine-grained control
 *
 * @example
 *   ```typescript
 *   constructor(private loadingService: GlobalLoadingService) {}
 *
 *   // Show loader with message
 *   this.loadingService.show('Loading data...');
 *
 *   // Show loader with metadata context
 *   this.loadingService.showWithContext({
 *     feature: 'member-info',
 *     action: 'fetch',
 *     message: 'Fetching member details...'
 *   });
 *
 *   // Hide loader
 *   this.loadingService.hide();
 *
 *   // Subscribe to loading state
 *   this.loadingService.isLoading().subscribe(isLoading => {
 *     console.log('Loading:', isLoading);
 *   });
 *   ```;
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalLoadingService {
  /** Internal signal tracking loading state */
  private readonly loadingState = signal<boolean>(false);

  /** Internal signal tracking loading message */
  private readonly loadingMessage = signal<string | undefined>(undefined);

  /** Internal signal tracking loading context */
  private readonly loadingContext = signal<LoadingContext | undefined>(undefined);

  /** Internal signal tracking all active loading contexts */
  private readonly activeContextsSignal = signal<readonly LoadingContext[]>([]);

  /** Signal tracking whether debug message should be shown beneath loader */
  private readonly debugMessageEnabledSignal = signal<boolean>(this.getInitialDebugMessageState());

  /** Active loading contexts stack */
  private readonly activeContexts: LoadingContext[] = [];

  /** Count of active HTTP requests */
  private activeRequests = 0;

  /** Debounce timer to prevent flashing */
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  /** Minimum time (ms) to show loader to prevent flashing */
  private readonly minDisplayTime = 300;

  /** Timestamp when loader was shown */
  private shownAt: number | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEY_GLOBAL_LOADER_DEBUG_MESSAGE) {
          this.debugMessageEnabledSignal.set(event.newValue === 'true');
        }
      });
    }
  }

  private getInitialDebugMessageState(): boolean {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(STORAGE_KEY_GLOBAL_LOADER_DEBUG_MESSAGE) === 'true';
  }

  /** Observable signal indicating whether debug messages are enabled beneath the loader */
  readonly isDebugMessageEnabled = this.debugMessageEnabledSignal.asReadonly();

  /** Dynamically toggle or set debug messages enabled state */
  setDebugMessagesEnabled(enabled: boolean): void {
    this.debugMessageEnabledSignal.set(enabled);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_GLOBAL_LOADER_DEBUG_MESSAGE, enabled ? 'true' : 'false');
    }
  }

  /**
   * Observable loading state signal
   *
   * @returns Signal indicating whether loader is currently shown
   */
  readonly isLoading = this.loadingState.asReadonly();

  /**
   * Observable loading message signal
   *
   * @returns Signal containing current loading message (if any)
   */
  readonly message = this.loadingMessage.asReadonly();

  /**
   * Observable loading context signal
   *
   * @returns Signal containing current loading context metadata
   */
  readonly context = this.loadingContext.asReadonly();

  /**
   * Observable signal containing all currently active loading contexts
   *
   * @returns Signal with list of active LoadingContext objects
   */
  readonly contexts = this.activeContextsSignal.asReadonly();

  /**
   * Show the global loader
   *
   * @param message - Optional loading message to display
   */
  show(message?: string): void {
    this.activeRequests++;
    const ctx: LoadingContext = { message };
    this.activeContexts.push(ctx);
    this.activeContextsSignal.set([...this.activeContexts]);

    // Clear any pending hide
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Update message if provided
    if (message) {
      this.loadingMessage.set(message);
    }
    this.loadingContext.set(ctx);

    // Show loader immediately if not already shown
    if (!this.loadingState()) {
      this.shownAt = Date.now();
      this.loadingState.set(true);
    }
  }

  /**
   * Show the global loader with context metadata
   *
   * Allows fine-grained control over loading state with contextual information about the operation being performed.
   *
   * @example
   *   ```typescript
   *   this.loadingService.showWithContext({
   *     feature: 'member-info',
   *     action: 'fetch',
   *     message: 'Fetching member details...'
   *   });
   *   ```;
   *
   * @param context - Loading context metadata
   */
  showWithContext(context: LoadingContext): void {
    this.activeRequests++;
    this.activeContexts.push(context);
    this.activeContextsSignal.set([...this.activeContexts]);

    // Clear any pending hide
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Update context and message
    this.loadingContext.set(context);
    if (context.message) {
      this.loadingMessage.set(context.message);
    }

    // Show loader immediately if not already shown
    if (!this.loadingState()) {
      this.shownAt = Date.now();
      this.loadingState.set(true);
    }
  }

  /**
   * Hide the global loader
   *
   * Automatically handles request counting and minimum display time to prevent flashing for fast requests.
   */
  hide(): void {
    this.activeRequests = Math.max(0, this.activeRequests - 1);
    if (this.activeContexts.length > 0) {
      this.activeContexts.pop();
    }
    this.activeContextsSignal.set([...this.activeContexts]);

    if (this.activeContexts.length > 0) {
      const top = this.activeContexts[this.activeContexts.length - 1];
      this.loadingContext.set(top);
      this.loadingMessage.set(top.message);
    }

    // Only hide if no active requests
    if (this.activeRequests === 0) {
      this.hideWithDebounce();
    }
  }

  /**
   * Force hide the loader regardless of active request count
   *
   * Use with caution - typically only needed for error scenarios
   */
  forceHide(): void {
    this.activeRequests = 0;
    this.activeContexts.length = 0;
    this.activeContextsSignal.set([]);
    this.hideWithDebounce();
  }

  /**
   * Reset the service to initial state
   *
   * Clears all state including active request count
   */
  reset(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.activeRequests = 0;
    this.activeContexts.length = 0;
    this.activeContextsSignal.set([]);
    this.loadingState.set(false);
    this.loadingMessage.set(undefined);
    this.loadingContext.set(undefined);
    this.shownAt = null;
  }

  /**
   * Get the current active request count
   *
   * @returns Number of active HTTP requests
   */
  getActiveRequestCount(): number {
    return this.activeRequests;
  }

  /**
   * Hide loader with minimum display time enforcement
   *
   * Ensures loader is shown for at least minDisplayTime to prevent flashing
   *
   * @private
   */
  private hideWithDebounce(): void {
    if (!this.loadingState()) {
      return;
    }

    const now = Date.now();
    const elapsed = this.shownAt ? now - this.shownAt : this.minDisplayTime;
    const remainingTime = Math.max(0, this.minDisplayTime - elapsed);

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      // Only hide if no active requests (prevents race condition)
      if (this.activeRequests === 0) {
        this.loadingState.set(false);
        this.loadingMessage.set(undefined);
        this.loadingContext.set(undefined);
        this.shownAt = null;
      }
      this.debounceTimer = null;
    }, remainingTime);
  }
}
